<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Buildings2Icon } from '@solar-icons/vue/linear'
import AppMenu from '../../primitives/AppMenu/AppMenu.vue'
import AppAvatar from '../../primitives/AppAvatar/AppAvatar.vue'
import type { MenuItem } from '../../primitives/AppMenu/menu-types'
import type { NavBrand, NavUser } from './nav-types'
import AppTooltip from '../../primitives/AppTooltip/AppTooltip.vue'
import { useSidebar } from './useSidebar'

const props = withDefaults(
  defineProps<{
    brand?: NavBrand | null
    user?: NavUser | null
  }>(),
  { brand: null, user: null },
)

const emit = defineEmits<{
  'sign-out': []
}>()

const { state } = useSidebar()
const rail = computed(() => state.value === 'rail')
const router = useRouter()
const { t } = useI18n()

const brandName = computed(() => props.brand?.name?.trim() || undefined)
const label = computed(() => brandName.value ?? t('shell.brandPlaceholder'))
const initial = computed(() => brandName.value?.[0]?.toUpperCase())
const imageSrc = computed(() => props.brand?.monogramSrc ?? props.brand?.logoSrc)
const isLogo = computed(() => !props.brand?.monogramSrc && !!props.brand?.logoSrc)

const userInitials = computed(() => {
  const parts = props.user?.name.trim().split(/\s+/).filter(Boolean) ?? []
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
})

const buttonClass = computed(() => [
  'flex h-20 w-full items-center rounded-4 text-left outline-none hover:bg-bg-tertiary focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-blue',
  rail.value ? 'justify-center' : 'gap-4 px-2',
])

const menuItems = computed<MenuItem[]>(() => [
  { id: 'settings', label: t('nav.settings') },
  { id: 'signout', label: t('common.signOut'), danger: true },
])

function onSelect(id: string) {
  if (id === 'signout') emit('sign-out')
  if (id === 'settings') void router.push('/settings/account')
}
</script>

<template>
  <AppMenu
    v-if="user"
    :items="menuItems"
    :placement="rail ? 'right-start' : 'bottom-start'"
    @select="onSelect"
  >
    <template #trigger="{ aria }">
      <AppTooltip :content="rail ? label : ''" placement="right">
        <template #trigger="{ aria: tipAria }">
          <button
            type="button"
            :aria-label="label"
            v-bind="{ ...aria, ...tipAria }"
            :class="buttonClass"
          >
            <AppAvatar
              :name="label"
              :src="imageSrc"
              :initials="initial"
              size="xs"
              :fit="isLogo ? 'contain' : 'cover'"
              shape="square"
              tone="strong"
            >
              <Buildings2Icon v-if="!initial" aria-hidden="true" />
            </AppAvatar>
            <span v-if="!rail" class="flex min-w-0 flex-1 flex-col">
              <span
                class="truncate text-body-sm-emphasized"
                :class="brandName ? 'text-text-primary' : 'text-text-tertiary'"
              >
                {{ label }}
              </span>
              <span class="truncate text-caption text-text-tertiary">{{ user.name }}</span>
            </span>
          </button>
        </template>
      </AppTooltip>
    </template>
    <template #header>
      <div class="flex items-center gap-4 px-6 py-4">
        <AppAvatar
          :name="user.name"
          :src="user.avatarUrl"
          :initials="userInitials"
          size="xs"
          tone="strong"
        />
        <span class="flex min-w-0 flex-col">
          <span class="truncate text-body-sm-medium text-text-primary">{{ user.name }}</span>
          <span class="truncate text-caption text-text-tertiary">{{ user.email }}</span>
        </span>
      </div>
    </template>
  </AppMenu>

  <AppTooltip v-else :content="rail ? label : ''" placement="right">
    <template #trigger="{ aria }">
      <button type="button" :aria-label="label" v-bind="aria" :class="buttonClass">
        <AppAvatar
          :name="label"
          :src="imageSrc"
          :initials="initial"
          size="xs"
          :fit="isLogo ? 'contain' : 'cover'"
          shape="square"
          tone="strong"
        >
          <Buildings2Icon v-if="!initial" aria-hidden="true" />
        </AppAvatar>
        <span v-if="!rail" class="flex min-w-0 flex-1 flex-col">
          <span
            class="truncate text-body-sm-emphasized"
            :class="brandName ? 'text-text-primary' : 'text-text-tertiary'"
          >
            {{ label }}
          </span>
        </span>
      </button>
    </template>
  </AppTooltip>
</template>
